import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CalendarCheck, Clock, MapPin, Tag, ArrowRight, User, QrCode, Activity, ShieldCheck, X, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import BookingQRDialog from "@/components/site/BookingQRDialog";

const statusStyle = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

const Account = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [visits, setVisits] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [qrBookingId, setQrBookingId] = useState(null);
  const [checkinQr, setCheckinQr] = useState(null);
  const [checkinQrLoading, setCheckinQrLoading] = useState(true);
  const [regeneratingQr, setRegeneratingQr] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [b, v, m] = await Promise.all([
          api.get("/me/bookings", { withCredentials: true }),
          api.get("/me/visits", { withCredentials: true }),
          api.get("/me/membership", { withCredentials: true }),
        ]);
        setBookings(b.data);
        setVisits(v.data);
        setMyMembership(m.data);
      } catch {
        toast.error("Could not load your account.");
      } finally {
        setFetching(false);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/me/checkin-qr", { withCredentials: true });
        setCheckinQr(data);
      } catch {
        setCheckinQr(null);
      } finally {
        setCheckinQrLoading(false);
      }
    })();
  }, [loading, user, navigate]);

  const regenerateCheckinQr = async () => {
    setRegeneratingQr(true);
    try {
      const { data } = await api.post("/me/checkin-qr/regenerate", {}, { withCredentials: true });
      setCheckinQr(data);
      toast.success("New QR generated — check your email.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate QR.");
    } finally {
      setRegeneratingQr(false);
    }
  };

  const cancelMembership = async () => {
    if (!window.confirm("Cancel at the end of the current period? You'll keep access until then.")) return;
    try {
      await api.post("/me/membership/cancel", {}, { withCredentials: true });
      const { data } = await api.get("/me/membership", { withCredentials: true });
      setMyMembership(data);
      toast.success("Cancel scheduled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    }
  };

  const resumeMembership = async () => {
    try {
      await api.post("/me/membership/resume", {}, { withCredentials: true });
      const { data } = await api.get("/me/membership", { withCredentials: true });
      setMyMembership(data);
      toast.success("Resumed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume.");
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
      </div>
    );
  }

  return (
    <div className="bg-spike-surface min-h-screen" data-testid="account-page">
      <section className="relative pt-16 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light flex items-center gap-3">
              <span className="w-8 h-px bg-spike-blue" /> My account
            </div>
            <h1 className="mt-6 font-orbitron font-black uppercase text-4xl lg:text-5xl text-spike-text leading-tight">
              Welcome back, <span className="text-spike-blue-light">{user.name.split(" ")[0]}</span>.
            </h1>
            <p className="mt-4 font-exo text-spike-text-muted">{user.email}</p>
          </div>
          <div className="flex gap-3">
            {user.is_admin && (
              <Link
                to="/admin"
                data-testid="account-admin-link"
                className="inline-flex items-center gap-2 bg-transparent text-spike-text border border-spike-blue/60 hover:bg-spike-blue/10 font-orbitron text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
              >
                Admin
              </Link>
            )}
            <button
              type="button"
              data-testid="account-logout-btn"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="inline-flex items-center gap-2 bg-transparent text-spike-text-muted hover:text-spike-text border border-white/10 hover:border-white/15 font-orbitron text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* ===== Membership card ===== */}
          <div className="mb-10" data-testid="account-membership">
            <h2 className="font-orbitron font-bold uppercase tracking-wider text-spike-text text-xl mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-spike-blue-light" /> Membership
            </h2>
            {myMembership?.membership ? (
              <div className="bg-spike-elevated border border-spike-blue/40 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5 shadow-card-hover">
                <div>
                  <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.35em] text-spike-blue-light">Active plan</div>
                  <div className="mt-2 flex items-baseline gap-3">
                    <span className="font-orbitron font-black uppercase text-2xl text-spike-text">{myMembership.tier?.name}</span>
                    <span className="font-orbitron text-spike-blue-light font-bold">${myMembership.tier?.price_cad}<span className="text-spike-text-muted text-xs"> / {myMembership.tier?.period}</span></span>
                  </div>
                  <p className="mt-2 font-exo text-sm text-spike-text-muted">
                    Started {new Date(myMembership.membership.started_at).toLocaleDateString()}
                    {" "}· {myMembership.membership.cancel_at_period_end ? "Ends" : "Renews"} on{" "}
                    <strong className="text-spike-text">{new Date(myMembership.membership.current_period_end).toLocaleDateString()}</strong>
                  </p>
                  {myMembership.tier?.discount_percent > 0 && (
                    <p className="mt-1 font-exo text-xs text-spike-blue-light">
                      You're saving <strong>{myMembership.tier.discount_percent}%</strong> on every lane booking.
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to="/membership"
                    data-testid="account-change-plan"
                    className="inline-flex items-center gap-2 border border-spike-blue/40 hover:bg-spike-blue/10 text-spike-text font-orbitron text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
                  >
                    Change plan
                  </Link>
                  {myMembership.membership.cancel_at_period_end ? (
                    <button
                      type="button"
                      data-testid="account-resume-membership"
                      onClick={resumeMembership}
                      className="inline-flex items-center gap-2 bg-spike-blue text-black hover:bg-spike-blue-hover font-orbitron text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
                    >
                      Resume
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="account-cancel-membership"
                      onClick={cancelMembership}
                      className="inline-flex items-center gap-2 border border-red-400/50 hover:bg-red-500/10 text-spike-text font-orbitron text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-spike-elevated border border-dashed border-white/15 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="account-no-membership">
                <div>
                  <p className="font-orbitron uppercase tracking-wider text-spike-text">No active plan.</p>
                  <p className="mt-1 font-exo text-sm text-spike-text-muted">Members unlock discounted lanes, free monthly hours, and priority bookings.</p>
                </div>
                <Link
                  to="/membership"
                  data-testid="account-browse-plans"
                  className="inline-flex items-center gap-2 bg-spike-blue text-black hover:bg-spike-blue-hover font-orbitron text-[0.65rem] uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                >
                  Browse plans <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </div>

          <div className="mb-10" data-testid="account-checkin-qr">
            <h2 className="font-orbitron font-bold uppercase tracking-wider text-spike-text text-xl mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-spike-blue-light" /> Check-in QR
            </h2>
            {checkinQrLoading ? (
              <div className="bg-spike-elevated border border-white/10 p-8 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" />
              </div>
            ) : checkinQr?.display_token ? (
              <div className="bg-spike-elevated border border-spike-blue/30 p-6 flex flex-col md:flex-row md:items-center gap-6">
                <div className="bg-white p-4 rounded-md inline-block self-start">
                  <QRCodeSVG value={checkinQr.display_token} size={180} level="H" />
                </div>
                <div className="flex-1">
                  <p className="font-exo text-sm text-spike-text-muted">
                    Show this QR at the front desk when you arrive. You can also use the QR in your waiver confirmation email.
                  </p>
                  <p className="mt-3 font-mono text-xs text-spike-dim break-all">{checkinQr.display_token}</p>
                  <button
                    type="button"
                    data-testid="account-regenerate-qr"
                    disabled={regeneratingQr}
                    onClick={regenerateCheckinQr}
                    className="mt-4 inline-flex items-center gap-2 border border-white/15 hover:border-spike-blue text-spike-text font-orbitron text-[0.65rem] uppercase tracking-[0.2em] px-4 py-2.5 disabled:opacity-50"
                  >
                    {regeneratingQr ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Regenerate QR
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-spike-elevated border border-dashed border-white/15 p-6">
                <p className="font-exo text-sm text-spike-text-muted">
                  Sign the liability waiver to get your check-in QR.{" "}
                  <Link to="/checkin" className="text-spike-blue-light underline">
                    Sign waiver
                  </Link>
                </p>
              </div>
            )}
          </div>

          <div className="flex items-end justify-between mb-8">
            <h2 className="font-orbitron font-bold uppercase tracking-wider text-spike-text text-xl">
              My bookings
            </h2>
            <Link
              to="/book"
              data-testid="account-book-cta"
              className="inline-flex items-center gap-2 text-spike-blue-light hover:text-spike-text font-orbitron text-[0.7rem] uppercase tracking-[0.3em]"
            >
              Book another <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {fetching ? (
            <div className="py-16 flex justify-center text-spike-text-muted gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" /> Loading...
            </div>
          ) : bookings.length === 0 ? (
            <div className="border border-dashed border-white/10 p-16 text-center" data-testid="account-empty">
              <User className="w-10 h-10 text-spike-blue-light mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-orbitron uppercase tracking-wider text-spike-text">No bookings yet.</p>
              <p className="mt-2 font-exo text-spike-text-muted text-sm">
                Lock in your first session — we'll save it here.
              </p>
              <Link
                to="/book"
                className="mt-6 inline-flex items-center gap-2 bg-spike-blue text-white font-orbitron uppercase tracking-[0.22em] text-xs px-6 py-3"
              >
                Book a lane <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="account-bookings">
              {bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`booking-card-${b.id}`}
                  className="bg-spike-elevated border border-white/10 p-6 hover:border-spike-blue/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`text-[0.6rem] font-orbitron uppercase tracking-[0.3em] px-2 py-1 border ${
                        statusStyle[b.status] || statusStyle.pending
                      }`}
                    >
                      {b.status}
                    </span>
                    <span className="font-orbitron text-[0.6rem] uppercase tracking-[0.3em] text-spike-dim">
                      #{b.id.slice(0, 6).toUpperCase()}
                    </span>
                  </div>
                  <div className="font-orbitron font-bold text-spike-text text-lg uppercase tracking-wide capitalize">
                    {b.sport} · {b.lane_type.replace("_", " ")}
                  </div>
                  <ul className="mt-4 space-y-2 font-exo text-sm text-spike-text-muted">
                    <li className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-spike-blue-light" /> {b.venue_id}
                    </li>
                    <li className="flex items-center gap-2">
                      <CalendarCheck className="w-3.5 h-3.5 text-spike-blue-light" /> {b.date}
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-spike-blue-light" /> {b.time_slot} · {b.duration_minutes} min
                    </li>
                    {b.machine && (
                      <li className="flex items-center gap-2">
                        <Tag className="w-3.5 h-3.5 text-spike-blue-light" /> {b.machine.replace("_", " ")}
                      </li>
                    )}
                  </ul>
                  <div className="mt-5 flex gap-2">
                    <button
                      type="button"
                      data-testid={`booking-qr-${b.id}`}
                      onClick={() => setQrBookingId(b.id)}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-white/10 hover:border-spike-blue hover:bg-spike-blue/10 text-white font-orbitron text-[0.65rem] uppercase tracking-[0.22em] py-2.5 transition-colors"
                    >
                      <QrCode className="w-3.5 h-3.5" /> Show QR
                    </button>
                    <a
                      href={`/checkin/${b.id}`}
                      data-testid={`booking-checkin-${b.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron text-[0.65rem] uppercase tracking-[0.22em] py-2.5 transition-colors"
                    >
                      Check in <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <BookingQRDialog
        open={!!qrBookingId}
        onOpenChange={(o) => { if (!o) setQrBookingId(null); }}
        bookingId={qrBookingId}
        bookingRef={qrBookingId?.slice(0, 8).toUpperCase()}
      />

      <section className="py-12 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Activity className="w-5 h-5 text-spike-blue-light" strokeWidth={1.5} />
            <h2 className="font-orbitron font-bold uppercase tracking-wider text-spike-text text-xl">
              Visit history
            </h2>
            <span className="font-orbitron text-[0.65rem] uppercase tracking-[0.3em] text-spike-blue-light">
              {visits.length} {visits.length === 1 ? "visit" : "visits"}
            </span>
          </div>

          {fetching ? (
            <div className="py-10 flex justify-center text-spike-text-muted gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" /> Loading...
            </div>
          ) : visits.length === 0 ? (
            <div className="border border-dashed border-white/10 p-10 text-center" data-testid="visits-empty">
              <Activity className="w-8 h-8 text-spike-blue-light mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-orbitron uppercase tracking-wider text-spike-text text-sm">No visits logged yet.</p>
              <p className="mt-2 font-exo text-spike-text-muted text-xs">
                Your check-ins will appear here once you're on a lane.
              </p>
            </div>
          ) : (
            <ol className="relative border-l border-white/10 ml-3 space-y-5" data-testid="visits-timeline">
              {visits.map((v) => {
                const date = new Date(v.checked_in_at);
                return (
                  <li key={v.id} data-testid={`visit-${v.id}`} className="ml-6">
                    <span className="absolute -left-1.5 w-3 h-3 bg-spike-blue border-2 border-black rounded-full" />
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <div className="font-orbitron text-[0.65rem] uppercase tracking-[0.3em] text-spike-blue-light">
                        {date.toLocaleDateString()} · {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <span className="text-[0.6rem] font-orbitron uppercase tracking-[0.3em] px-2 py-0.5 border bg-spike-blue/10 text-spike-blue-light border-spike-blue/30">
                        {v.type === "booking_checkin" ? "Booking" : "Self"}
                      </span>
                    </div>
                    <div className="mt-1 font-exo text-sm text-spike-text capitalize">
                      {v.booking
                        ? `${v.booking.sport} · ${v.booking.lane_type?.replace("_", " ")} at ${v.booking.venue_id}`
                        : "Self check-in at venue"}
                    </div>
                    {v.booking && (
                      <div className="mt-0.5 text-xs text-spike-dim font-exo">
                        Booking #{v.booking_id.slice(0, 6).toUpperCase()} · {v.booking.date} {v.booking.time_slot}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
};

export default Account;
